import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, doc, updateDoc, getDoc } from 'firebase/firestore';
import { useParams } from 'react-router-dom';
import { db } from '../../firebaseConfig';
import { computeQaMetrics } from '../../lib/callables';

interface QAGates {
  minF1Overall: number;
  minF1PerKey: number;
  minAuditAgreement: number;
  minDataPositives: number;
}

interface QAMetrics {
  windowDays: number;
  overall: {
    precision: number;
    recall: number;
    f1: number;
  };
  perClass: {
    [className: string]: {
      precision: number;
      recall: number;
      f1: number;
      support: number;
    };
  };
  auditAgreement: number;
  data: {
    positives: number;
    negatives: number;
    totalLabeled: number;
  };
  drift: {
    classFreqDelta: number;
  };
  fpExamples: string[];
  fnExamples: string[];
  updatedAt: any;
}

interface TaskReadiness {
  score: number;
  status: 'Not Ready' | 'Improving' | 'Ready';
  gates: {
    overallF1: boolean;
    perKeyF1: boolean;
    auditAgreement: boolean;
    dataPositives: boolean;
  };
  updatedAt: any;
}

interface Task {
  id: string;
  title: string;
  qaGates?: QAGates;
  readiness?: number;
  readinessStatus?: string;
  readinessGates?: any;
  autoRules?: {
    enabled: boolean;
    canaryPct: number;
    modelId?: string;
  };
}

interface Model {
  id: string;
  taskId: string;
  status: string;
  metrics: any;
  createdAt: any;
}

interface Submission {
  id: string;
  mediaUrl?: string;
  imageUrl?: string;
  status: string;
  aiSignals?: {
    score: number;
    label: string;
    version: string;
    candidateGroup?: string;
  };
}

const ModelQAPage: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const [task, setTask] = useState<Task | null>(null);
  const [metrics, setMetrics] = useState<QAMetrics | null>(null);
  const [fpExamples, setFpExamples] = useState<Submission[]>([]);
  const [fnExamples, setFnExamples] = useState<Submission[]>([]);
  const [computing, setComputing] = useState(false);
  const [showGatesConfig, setShowGatesConfig] = useState(false);
  const [gatesConfig, setGatesConfig] = useState<QAGates>({
    minF1Overall: 0.85,
    minF1PerKey: 0.80,
    minAuditAgreement: 0.90,
    minDataPositives: 150
  });

  // Load task data
  useEffect(() => {
    if (!taskId) return;
    
    const unsubscribe = onSnapshot(doc(db, 'tasks', taskId), (doc) => {
      if (doc.exists()) {
        const taskData = { id: doc.id, ...doc.data() } as Task;
        setTask(taskData);
        
        if (taskData.qaGates) {
          setGatesConfig(taskData.qaGates);
        }
      }
    });

    return unsubscribe;
  }, [taskId]);

  // Load QA metrics
  useEffect(() => {
    if (!taskId) return;
    
    const unsubscribe = onSnapshot(
      doc(db, 'taskQa', taskId, 'metrics', 'latest'),
      (doc) => {
        if (doc.exists()) {
          setMetrics(doc.data() as QAMetrics);
        }
      }
    );

    return unsubscribe;
  }, [taskId]);


  // Load FP/FN examples
  useEffect(() => {
    if (metrics && metrics.fpExamples && metrics.fpExamples.length > 0) {
      loadExamples(metrics.fpExamples, setFpExamples);
    }
    if (metrics && metrics.fnExamples && metrics.fnExamples.length > 0) {
      loadExamples(metrics.fnExamples, setFnExamples);
    }
  }, [metrics]);

  const loadExamples = async (submissionIds: string[], setExamples: React.Dispatch<React.SetStateAction<Submission[]>>) => {
    const examples: Submission[] = [];
    for (const id of submissionIds.slice(0, 12)) { // Limit to 12 examples
      try {
        const docRef = doc(db, 'submissions', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          examples.push({ id: docSnap.id, ...docSnap.data() } as Submission);
        }
      } catch (error) {
        console.error(`Error loading submission ${id}:`, error);
      }
    }
    setExamples(examples);
  };

  const handleComputeMetrics = async () => {
    if (!taskId) return;
    
    setComputing(true);
    try {
      await computeQaMetrics({ taskId });
      alert('Metrics computed successfully!');
    } catch (error) {
      console.error('Error computing metrics:', error);
      alert('Failed to compute metrics');
    } finally {
      setComputing(false);
    }
  };

  const handleSaveGates = async () => {
    if (!task || !taskId) return;

    try {
      await updateDoc(doc(db, 'tasks', taskId), {
        qaGates: gatesConfig
      });
      setShowGatesConfig(false);
      alert('QA Gates updated successfully!');
    } catch (error) {
      console.error('Error saving gates:', error);
      alert('Failed to save gates');
    }
  };

  const handleEnableAuto = async () => {
    if (!task || !taskId) return;

    try {
      await updateDoc(doc(db, 'tasks', taskId), {
        'autoRules.enabled': true
      });
      alert('Auto-approval enabled!');
    } catch (error) {
      console.error('Error enabling auto-approval:', error);
      alert('Failed to enable auto-approval');
    }
  };

  const getReadinessColor = (score: number) => {
    if (score < 60) return 'bg-red-100 text-red-800';
    if (score < 90) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  const getReadinessStatus = (score: number) => {
    if (score < 60) return 'Not Ready';
    if (score < 90) return 'Improving';
    return 'Ready';
  };

  if (!taskId) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 text-6xl mb-4">⚠️</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Task ID Required</h3>
        <p className="text-gray-500">Please select a task to view Model & QA metrics.</p>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 text-6xl mb-4">❌</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Task Not Found</h3>
        <p className="text-gray-500">The requested task could not be found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Model & QA</h1>
          <p className="text-gray-600">Task: {task.title}</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleComputeMetrics}
            disabled={computing}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {computing ? 'Computing...' : 'Recompute Metrics'}
          </button>
          {task.readinessGates && Object.values(task.readinessGates).every(Boolean) && (
            <button
              onClick={handleEnableAuto}
              disabled={task.autoRules?.enabled}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {task.autoRules?.enabled ? 'Auto-Approval Enabled' : 'Make Auto'}
            </button>
          )}
        </div>
      </div>

      {/* Readiness Meter */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Readiness Meter</h3>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getReadinessColor(task.readiness || 0)}`}>
            {getReadinessStatus(task.readiness || 0)}
          </span>
        </div>
        
        <div className="w-full bg-gray-200 rounded-full h-8 mb-4">
          <div 
            className="bg-blue-600 h-8 rounded-full transition-all duration-300"
            style={{ width: `${Math.min(100, Math.max(0, task.readiness || 0))}%` }}
          />
        </div>
        
        <div className="text-3xl font-bold text-gray-900 mb-2">
          {task.readiness || 0}/100
        </div>
        
        <div className="text-sm text-gray-600">
          Overall F1: {metrics?.overall.f1 ? (metrics.overall.f1 * 100).toFixed(1) + '%' : 'N/A'} • 
          Audit Agreement: {metrics?.auditAgreement ? (metrics.auditAgreement * 100).toFixed(1) + '%' : 'N/A'} • 
          Data Points: {metrics?.data.positives || 0}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* QA Gates Configuration */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">QA Gates</h3>
            <button
              onClick={() => setShowGatesConfig(!showGatesConfig)}
              className="text-blue-600 hover:text-blue-900 text-sm"
            >
              {showGatesConfig ? 'Hide' : 'Edit'}
            </button>
          </div>

          {showGatesConfig ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Min Overall F1
                </label>
                <input
                  type="number"
                  min="0"
                  max="1"
                  step="0.01"
                  value={gatesConfig.minF1Overall}
                  onChange={(e) => setGatesConfig(prev => ({
                    ...prev,
                    minF1Overall: parseFloat(e.target.value)
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Min Per-Key F1
                </label>
                <input
                  type="number"
                  min="0"
                  max="1"
                  step="0.01"
                  value={gatesConfig.minF1PerKey}
                  onChange={(e) => setGatesConfig(prev => ({
                    ...prev,
                    minF1PerKey: parseFloat(e.target.value)
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Min Audit Agreement
                </label>
                <input
                  type="number"
                  min="0"
                  max="1"
                  step="0.01"
                  value={gatesConfig.minAuditAgreement}
                  onChange={(e) => setGatesConfig(prev => ({
                    ...prev,
                    minAuditAgreement: parseFloat(e.target.value)
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Min Data Positives
                </label>
                <input
                  type="number"
                  min="0"
                  value={gatesConfig.minDataPositives}
                  onChange={(e) => setGatesConfig(prev => ({
                    ...prev,
                    minDataPositives: parseInt(e.target.value)
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={handleSaveGates}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Save Gates
                </button>
                <button
                  onClick={() => setGatesConfig({
                    minF1Overall: 0.85,
                    minF1PerKey: 0.80,
                    minAuditAgreement: 0.90,
                    minDataPositives: 150
                  })}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Restore Defaults
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Overall F1</span>
                <span className="text-sm font-medium">{gatesConfig.minF1Overall}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Per-Key F1</span>
                <span className="text-sm font-medium">{gatesConfig.minF1PerKey}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Audit Agreement</span>
                <span className="text-sm font-medium">{gatesConfig.minAuditAgreement}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Data Positives</span>
                <span className="text-sm font-medium">{gatesConfig.minDataPositives}</span>
              </div>
            </div>
          )}
        </div>

        {/* Live Metrics */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Live Metrics (Last 30d)</h3>
          
          {metrics ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {(metrics.overall.f1 * 100).toFixed(1)}%
                  </div>
                  <div className="text-sm text-gray-600">Overall F1</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {(metrics.auditAgreement * 100).toFixed(1)}%
                  </div>
                  <div className="text-sm text-gray-600">Audit Agreement</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {metrics.data.positives}
                  </div>
                  <div className="text-sm text-gray-600">Positives</div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Per-Class Metrics</h4>
                <div className="space-y-1">
                  {Object.entries(metrics.perClass).map(([className, classMetrics]) => (
                    <div key={className} className="flex justify-between text-sm">
                      <span className="text-gray-600">{className}</span>
                      <span className="font-medium">
                        F1: {(classMetrics.f1 * 100).toFixed(1)}% ({classMetrics.support} samples)
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {metrics.drift.classFreqDelta > 0.1 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                  <div className="flex items-center">
                    <span className="text-yellow-600 text-sm font-medium">⚠️ Drift Detected</span>
                  </div>
                  <div className="text-sm text-yellow-700 mt-1">
                    Class frequency shift: {(metrics.drift.classFreqDelta * 100).toFixed(1)}%
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-400 text-4xl mb-2">📊</div>
              <p className="text-gray-500">No metrics available. Click "Recompute Metrics" to generate.</p>
            </div>
          )}
        </div>
      </div>

      {/* Examples Gallery */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Examples Gallery</h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* False Positives */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">False Positives ({fpExamples.length})</h4>
            {fpExamples.length > 0 ? (
              <div className="grid grid-cols-3 gap-2">
                {fpExamples.map((submission) => (
                  <div key={submission.id} className="relative group cursor-pointer">
                    <img
                      src={submission.mediaUrl || submission.imageUrl}
                      alt="FP Example"
                      className="w-full h-20 object-cover rounded border"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/placeholder-image.jpg';
                      }}
                    />
                    <div className="absolute inset-0 bg-red-500 bg-opacity-20 rounded opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="absolute bottom-1 left-1 text-xs text-white bg-red-600 px-1 rounded">
                      FP
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <div className="text-4xl mb-2">✅</div>
                <p>No false positives found</p>
              </div>
            )}
          </div>

          {/* False Negatives */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">False Negatives ({fnExamples.length})</h4>
            {fnExamples.length > 0 ? (
              <div className="grid grid-cols-3 gap-2">
                {fnExamples.map((submission) => (
                  <div key={submission.id} className="relative group cursor-pointer">
                    <img
                      src={submission.mediaUrl || submission.imageUrl}
                      alt="FN Example"
                      className="w-full h-20 object-cover rounded border"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/placeholder-image.jpg';
                      }}
                    />
                    <div className="absolute inset-0 bg-blue-500 bg-opacity-20 rounded opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="absolute bottom-1 left-1 text-xs text-white bg-blue-600 px-1 rounded">
                      FN
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <div className="text-4xl mb-2">✅</div>
                <p>No false negatives found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModelQAPage;

